/* eslint-disable prefer-const */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { authService } from "../../services/auth.service";
import { redirectToSignIn } from "../../util/signInRedirect";
import { useAuthCheck } from "../../hooks/useAuthCheck";
import { Spinner, Typography, TypographyVariant } from "../ui-kit";
import { FenaLogo } from "../icons";
import { AnimateAppearanceWrapper } from "../animate-appearence/AnimateAppearanceWrapper";
import Api from "../../util/api";
import { QueryKeys } from "../../util/types";
import { useModalManager } from "../../context/ModalManager";
import {
  isModalDismissedInCurrentSession,
  getSessionStartedAt,
  setSessionStartedAt,
  clearSessionStartedAt,
  getSessionRemainingMs,
} from "../../util/session-utils";
import { Enforce2FAModal } from "../../modules/private/profile-module/profile-settings/components/Enforce2FAModal";
import {
  canAccessPrivatePathByPolicies,
  getFirstAllowedPrivateRoute,
  hasAnyAdministratorViewPolicy,
} from "../../util/administratorRouteAccess";

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f8f9fa;
`;

const LogoWrapper = styled.div`
  margin-bottom: 40px;
  animation: fadeIn 0.5s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledSpinner = styled(Spinner)`
  margin-bottom: 20px;
`;

const LoadingText = styled(Typography)`
  color: #495b6c;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

interface AuthGateProps {
  children: React.ReactNode;
}

type AuthState = "initializing" | "authenticated" | "unauthenticated";

/**
 * AuthGate - Centralized authentication wrapper for admin panel
 *
 * This component handles authentication logic:
 * 1. Checks if user session is valid
 * 2. If session is valid - shows children
 * 3. If session is invalid - clears localStorage and redirects to login
 * 4. Preserves session on page reload if session is still valid
 */
export const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkSignInStatus } = useAuthCheck();
  const { addModal } = useModalManager();
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>("initializing");
  const [isReady, setIsReady] = useState(false);
  const mfaCheckDone = useRef(false);

  // Public paths that don't require authentication
  const publicPaths = [
    "/sign-in",
    "/sign-up",
    "/reset-password",
    "/verify/",
    "/redirects",
  ];

  const isPublicPath = (path: string) => {
    return (
      publicPaths.some((publicPath) => path.startsWith(publicPath)) ||
      path === "/"
    );
  };

  // Global handler for "auth expired" (e.g. refresh token revoked/expired, or double 401).
  // The API layer dispatches this event to force a clean redirect.
  useEffect(() => {
    const onExpired = () => {
      clearSessionStartedAt();
      setAuthState("unauthenticated");
      setIsReady(true);
      if (!isPublicPath(location.pathname)) {
        redirectToSignIn(navigate, location);
      }
    };

    window.addEventListener("fena:auth-expired", onExpired);
    return () => {
      window.removeEventListener("fena:auth-expired", onExpired);
    };
  }, [location]);

  // Cached data belongs to the signed-in administrator: drop it once the app
  // leaves the private area (sign-out, expired session), so the next
  // administrator never sees it. Private pages are unmounted by then, so
  // nothing refetches without a token. The auth user query is kept: it is
  // mounted for the whole app and only feeds the context when it changes.
  useEffect(() => {
    if (location.pathname.startsWith("/private")) return;
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== QueryKeys.GET_AUTH_USER,
    });
  }, [location.pathname]);

  // Proactive token refresh while user is active:
  // - ID tokens expire regularly (~1h). Firebase can refresh them as long as the session is valid.
  // - Refreshing proactively avoids random 401s during long admin sessions.
  useEffect(() => {
    if (authState !== "authenticated") return;

    let intervalId: number | undefined;
    let stopped = false;

    const refresh = async (force = false) => {
      try {
        // Force-refresh occasionally to keep the ID token fresh.
        const { googleIdentityService } = await import(
          "../../services/google-identity"
        );
        const token = await googleIdentityService.getIdToken(force);
        if (!token) {
          window.dispatchEvent(new CustomEvent("fena:auth-expired"));
          return;
        }
        Api.updateHeadersWithToken(token);
      } catch {
        window.dispatchEvent(new CustomEvent("fena:auth-expired"));
      }
    };

    // Refresh on focus / visibility (cheap and aligns with "user is using the app")
    const onFocus = () => refresh(false);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh(false);
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    // Force refresh every 45 minutes while authenticated.
    intervalId = window.setInterval(() => {
      if (!stopped) refresh(true);
    }, 45 * 60 * 1000);

    // Do an initial refresh soon after auth (non-forced)
    refresh(false);

    return () => {
      stopped = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [authState]);

  // Absolute session expiration: force sign-out exactly 30 minutes after
  // sign-in, regardless of user activity. The timestamp is set on sign-in
  // (see auth.service.ts) and cleared on sign-out.
  useEffect(() => {
    if (authState !== "authenticated") return;

    // Grace period for sessions that started before this check existed.
    if (getSessionStartedAt() === null) {
      setSessionStartedAt();
    }

    const remaining = getSessionRemainingMs();

    const expireSession = async () => {
      clearSessionStartedAt();
      try {
        await authService.signOut();
      } catch (e) {
        console.warn("Session expiration sign-out failed:", e);
      }
      window.dispatchEvent(new CustomEvent("fena:auth-expired"));
    };

    if (remaining <= 0) {
      expireSession();
      return;
    }

    const timeoutId = window.setTimeout(expireSession, remaining);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authState]);

  // 2FA enforcement: show modal once per session if user hasn't enrolled
  useEffect(() => {
    if (authState !== "authenticated" || mfaCheckDone.current) return;
    mfaCheckDone.current = true;

    const check2FA = async () => {
      if (isModalDismissedInCurrentSession()) return;
      try {
        const has2FA = await authService.has2FAEnabled();
        if (!has2FA) {
          addModal(<Enforce2FAModal key="enforce-2fa" has2FA={false} />);
        }
      } catch (e) {
        console.warn("2FA check failed:", e);
      }
    };

    check2FA();
  }, [authState]);

  useEffect(() => {
    const performAuthCheck = async () => {
      try {
        // If already on a public path, skip auth check and render
        if (isPublicPath(location.pathname)) {
          setAuthState("unauthenticated");
          setIsReady(true);
          return;
        }

        // Wait for Firebase/Identity Platform init (no poll — service awaits initPromise)
        const { googleIdentityService } = await import(
          "../../services/google-identity"
        );
        await googleIdentityService.isAvailable();

        // Check if user has a valid auth token
        const authUser = await authService.getCurrentUser();

        if (!authUser) {
          // If trying to access private route, redirect to sign-in
          if (location.pathname.startsWith("/private")) {
            redirectToSignIn(navigate, location);
            return;
          }
          setAuthState("unauthenticated");
          setIsReady(true);
          return;
        }

        console.log("🚪 AuthGate: User token found, validating session...");

        // If user is authenticated via GCP, trust the Firebase token and validate session
        if (authUser.provider === "gcp") {
          try {
            const { user } = await checkSignInStatus();

            if (!user) {
              console.log(
                "🚪 AuthGate: Session validation failed - user data not available"
              );
              // Session is invalid, redirect to sign-in (don't clear storage aggressively)
              setAuthState("unauthenticated");
              if (location.pathname.startsWith("/private")) {
                redirectToSignIn(navigate, location);
              }
              setIsReady(true);
              return;
            }

            console.log("🚪 AuthGate: Session is valid");
            setAuthState("authenticated");

            const firstAllowedRoute = getFirstAllowedPrivateRoute(
              user.policies
            );
            const hasViewAccess = hasAnyAdministratorViewPolicy(user.policies);
            if (!firstAllowedRoute || !hasViewAccess) {
              await authService.signOut();
              setAuthState("unauthenticated");
              setIsReady(true);
              redirectToSignIn(navigate, location);
              return;
            }

            // If user is on landing page or sign-in, redirect to dashboard
            if (
              location.pathname === "/" ||
              location.pathname.startsWith("/sign-in")
            ) {
              console.log(
                "🚪 AuthGate: Redirecting to first allowed module..."
              );
              navigate(firstAllowedRoute);
              return;
            }

            if (
              !canAccessPrivatePathByPolicies(location.pathname, user.policies)
            ) {
              navigate(firstAllowedRoute, { replace: true });
              return;
            }

            // User is authenticated and already on a private route
            setIsReady(true);
          } catch (error) {
            console.error("🚪 AuthGate: Error validating session:", error);
            // Session validation failed, but don't clear storage - just redirect
            setAuthState("unauthenticated");
            if (location.pathname.startsWith("/private")) {
              redirectToSignIn(navigate, location);
            }
            setIsReady(true);
          }
        } else {
          // Non-GCP provider (shouldn't happen in admin panel, but handle gracefully)
          setAuthState("unauthenticated");
          if (location.pathname.startsWith("/private")) {
            redirectToSignIn(navigate, location);
          }
          setIsReady(true);
        }
      } catch (error) {
        console.error("🚪 AuthGate: Fatal error in auth check:", error);
        // On error, if on private route, redirect to sign-in
        if (location.pathname.startsWith("/private")) {
          redirectToSignIn(navigate, location);
        } else {
          setAuthState("unauthenticated");
          setIsReady(true);
        }
      }
    };

    performAuthCheck();
  }, [location.pathname]); // Re-run only when path changes

  // Show loading screen while checking auth
  if (!isReady) {
    return (
      <LoadingWrapper>
        <LogoWrapper>
          <FenaLogo />
        </LogoWrapper>
        <AnimateAppearanceWrapper>
          <StyledSpinner size={60} />
        </AnimateAppearanceWrapper>
        <LoadingText variant={TypographyVariant.BODY1}>
          Checking your session...
        </LoadingText>
      </LoadingWrapper>
    );
  }

  // Render children only if authenticated
  if (authState === "authenticated") {
    return <>{children}</>;
  }

  // If unauthenticated and on public path, render children (login page, etc.)
  if (authState === "unauthenticated" && isPublicPath(location.pathname)) {
    return <>{children}</>;
  }

  if (!location.pathname.startsWith("/private")) {
    return <>{children}</>;
  }

  // Otherwise, don't render anything (redirect is in progress)
  return null;
};
